import {type Action} from '@reduxjs/toolkit';
import {BehaviorSubject} from 'rxjs';

import {policies} from "@isudio/config";
import type {IUser, IUserSignupResponse, IUserSession, IAsset, IAssetFeed, MediaObject} from "@isudio/data";
import {MenuItems} from "../constants/menu";

export type AnyPayloadAction = Action & {
  group?: string;
  [key: string]: any;
};

export interface StoreState {
  App: AppState;
  Db: DbState;
  Data: DataState;
  Device: DeviceState;
  User: UserState;
}

export interface AppState {
  show3DHome: boolean;
  cacheLoaded: boolean;

  menuClicked$: BehaviorSubject<boolean>;
  showMenu$: BehaviorSubject<boolean>;
  mousePosition$: BehaviorSubject<any>;
  menuRefresh$: BehaviorSubject<number>;
  menuItems$$: Array<any>;

  state$: BehaviorSubject<any>;
  settings: any;
  subscribers: any;

  dbReady: boolean;
  deviceReady: boolean;
  cacheReady: boolean;
  storeReady: boolean;

  activeDialogId: string | null;

  language: string;

  isOnline: boolean;

  noImage: any;
}

export interface DbState {
  dbReady: boolean;
  designSynced: boolean;
  dataReady: boolean;
  feed: any;
  sync: any;
}


export interface DataState {
  countries$: BehaviorSubject<any>;
  userPolicy$: BehaviorSubject<any>;
  privacyPolicy$: BehaviorSubject<any>;
  termsOfUse$: BehaviorSubject<any>;
  userSettings$: BehaviorSubject<any>;
  team$: BehaviorSubject<IUser[] | null>;
  assets$: BehaviorSubject<IAsset[] | null>;
  assets: IAsset[] | null;
  assetFeeds$: BehaviorSubject<IAssetFeed[] | null>;
  assetFeeds: IAsset[] | null;
  assetAuthors$: BehaviorSubject<any[] | null>;
  assetAuthors: any[] | null;
}

export interface DeviceState {
  data: any;
}

export interface UserState {
  user: IUserSignupResponse | IUser | null;
  user$: BehaviorSubject<IUserSignupResponse | IUser | null>;
  userPhoto: MediaObject | null;
  userPhoto$: BehaviorSubject<MediaObject>;
  session: IUserSession | null;
  session$: BehaviorSubject<IUserSession | null>;
  activationPending$: BehaviorSubject<boolean>;
  signedIn$: BehaviorSubject<boolean>;
}

export let storeState: StoreState = {
  App: {
    show3DHome: true,
    cacheLoaded: false,

    menuClicked$: new BehaviorSubject<boolean>(false),
    showMenu$: new BehaviorSubject<boolean>(false),
    mousePosition$: new BehaviorSubject<any>({x: 0, y: 0}),
    menuRefresh$: new BehaviorSubject<number>(0),
    menuItems$$: MenuItems,

    state$: new BehaviorSubject({
      window: {
        width: (typeof window !== 'undefined' ? window.screen.availWidth : null),
        height: (typeof window !== 'undefined' ? window.screen.availHeight : null),
        orientation: (typeof window !== 'undefined' ? window.screen.orientation.type.substring(0, window.screen.orientation.type.indexOf('-')) : null)
      }
    }),

    settings: {
      videos: {
        autoplay: false,
        muted: true
      }
    },

    subscribers: {},

    dbReady: false,
    deviceReady: false,
    cacheReady: false,
    storeReady: false,

    activeDialogId: null,

    language: 'en',

    isOnline: false,

    noImage: null,
  },

  Db: {
    dbReady: false,
    designSynced: false,
    dataReady: false,
    feed: {},
    sync: {},
  },

  Data: {
    countries$: new BehaviorSubject<any>(null),
    userPolicy$: new BehaviorSubject(policies.userPolicy),
    privacyPolicy$: new BehaviorSubject(policies.privacyPolicy),
    termsOfUse$: new BehaviorSubject(policies.termsOfUse),
    userSettings$: new BehaviorSubject<any>(null),
    team$: new BehaviorSubject<IUser[] | null>(null),
    assets$: new BehaviorSubject<IAsset[] | null>(null),
    assets: null,
    assetFeeds$: new BehaviorSubject<IAssetFeed[] | null>(null),
    assetFeeds: null,
    assetAuthors$: new BehaviorSubject<any[] | null>(null),
    assetAuthors: null
  },

  Device: {
    data: null
  },

  User: {
    user: null,
    user$: new BehaviorSubject<IUserSignupResponse | IUser | null>(null),
    userPhoto: null,
    userPhoto$: new BehaviorSubject<any>(null),
    session: null,
    session$: new BehaviorSubject<IUserSession | null>(null),
    activationPending$: new BehaviorSubject<boolean>(false),
    signedIn$: new BehaviorSubject<boolean>(false),
  }
};
